import type { ButtonProps, GroupProps, InputProps } from "@chakra-ui/react";

import { forwardRef, useRef } from "react";

import {
  IconButton,
  Input,
  InputGroup,
  mergeRefs,
  useControllableState,
} from "@chakra-ui/react";

import { Icon } from "@shared/ui/chakra/data-display/icon";

export interface PasswordVisibilityProps {
  /**
   * The default visibility state of the password input.
   */
  defaultVisible?: boolean;
  /**
   * Custom icons for the visibility toggle button.
   */
  visibilityIcon?: { off: React.ReactNode; on: React.ReactNode };
  /**
   * The controlled visibility state of the password input.
   */
  visible?: boolean;
  /**
   * Callback invoked when the visibility state changes.
   */
  onVisibleChange?: (visible: boolean) => void;
}

export interface PasswordInputProps
  extends InputProps, PasswordVisibilityProps {
  rootProps?: GroupProps;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, ref) => {
    const {
      defaultVisible,
      rootProps,
      visibilityIcon = {
        off: <Icon color="black" height="16px" name="EyeOff" width="16px" />,
        on: <Icon color="black" height="16px" name="Eye" width="16px" />,
      },
      visible: visibleProp,
      onVisibleChange,
      ...rest
    } = props;

    const [visible, setVisible] = useControllableState({
      defaultValue: defaultVisible || false,
      value: visibleProp,
      onChange: onVisibleChange,
    });

    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <InputGroup
        endElement={
          <VisibilityTrigger
            disabled={rest.disabled}
            onPointerDown={(e) => {
              if (rest.disabled) {
                return;
              }
              if (e.button !== 0) {
                return;
              }
              e.preventDefault();
              setVisible(!visible);
            }}
          >
            {visible ? visibilityIcon.off : visibilityIcon.on}
          </VisibilityTrigger>
        }
        {...rootProps}
      >
        <Input
          {...rest}
          ref={mergeRefs(ref, inputRef)}
          type={visible ? "text" : "password"}
        />
      </InputGroup>
    );
  },
);

const VisibilityTrigger = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    return (
      <IconButton
        aria-label="Toggle password visibility"
        aspectRatio="square"
        height="calc(100% - {spacing.2})"
        me="-2"
        ref={ref}
        size="sm"
        tabIndex={-1}
        variant="ghost"
        {...props}
      />
    );
  },
);
